import { useEffect } from 'react';

import { UseFormReturn, useWatch } from 'react-hook-form';

import { parseFormNumber, roundToPrecision } from '@/helpers/finance';
import { useCurrencies } from '@/modules/common/hooks';
import { FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES } from '@/modules/finance/common/constants/rounding.constants';

import { BillDetails, BillMonthlySalaryAvailability } from '../../types';
import { getBillTotal } from '../../utils';
import {
  BillValidationIssueCode,
  UPDATE_BILL_MESSAGE_BY_ISSUE,
  UpdateBillFormValues,
} from '../../validations';
import { BillGeneralInformationCard } from './bill-general-information-card';
import { BillLineItemsCard } from './bill-line-items-card';
import { BillTotalsCard } from './bill-totals-card';

const NEGATIVE_TOTAL_ERROR_MESSAGE =
  UPDATE_BILL_MESSAGE_BY_ISSUE[BillValidationIssueCode.NegativeTotal];

interface BillDetailsFormProps {
  details: BillDetails;
  form: UseFormReturn<UpdateBillFormValues>;
  isReadOnly: boolean;
  monthlySalaryAvailability: BillMonthlySalaryAvailability | undefined;
  isMonthlySalaryAvailabilityLoading: boolean;
}

/** Compact presentation/form composition for a bill; individual rows keep line editing isolated. */
export function BillDetailsForm({
  details,
  form,
  isReadOnly,
  monthlySalaryAvailability,
  isMonthlySalaryAvailabilityLoading,
}: BillDetailsFormProps) {
  const { currencies, isLoadingCurrencies } = useCurrencies();
  const items = useWatch({ control: form.control, name: 'items' });
  const currencyCode = useWatch({ control: form.control, name: 'currencyCode' });
  const isMonthlySalaryEnabled = useWatch({
    control: form.control,
    name: 'monthlySalaryEnabled',
  });
  const monthlySalaryAmount = monthlySalaryAvailability?.monthlySalaryAmount ?? null;
  const isMonthlySalaryAvailable = Boolean(
    monthlySalaryAvailability?.isAvailable && monthlySalaryAmount
  );
  const total = getBillTotal({
    items,
    monthlySalaryEnabled: isMonthlySalaryEnabled,
    monthlySalaryAmount,
  });
  const isNegativeTotalError = total < 0;

  useEffect(() => {
    if (isReadOnly || !isMonthlySalaryEnabled || isMonthlySalaryAvailable) return;
    form.setValue('monthlySalaryEnabled', false, { shouldDirty: true, shouldValidate: true });
  }, [form, isMonthlySalaryAvailable, isMonthlySalaryEnabled, isReadOnly]);

  useEffect(() => {
    if (isReadOnly) return;
    void form.trigger('items');
  }, [form, isReadOnly, items]);

  const balance = roundToPrecision(
    total - parseFormNumber(details.amountPaid),
    FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
  );

  return (
    <div className="grid w-full max-w-screen-2xl grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="flex flex-col gap-6">
        <BillGeneralInformationCard
          details={details}
          form={form}
          isReadOnly={isReadOnly}
          currencyOptions={currencies.map(currency => ({
            value: currency.code,
            label: currency.toString(),
          }))}
          isLoadingCurrencies={isLoadingCurrencies}
        />
        <BillLineItemsCard
          details={details}
          form={form}
          isReadOnly={isReadOnly}
          currencyCode={currencyCode}
          monthlySalaryAvailability={monthlySalaryAvailability}
          isMonthlySalaryAvailabilityLoading={isMonthlySalaryAvailabilityLoading}
        />
      </div>
      <BillTotalsCard
        totalAmount={isReadOnly ? details.totalAmount : total.toFixed(2)}
        amountPaid={details.amountPaid}
        balance={isReadOnly ? details.balance : balance.toFixed(2)}
        currencyCode={isReadOnly ? details.currencyCode : currencyCode}
        totalErrorMessage={
          !isReadOnly && isNegativeTotalError ? NEGATIVE_TOTAL_ERROR_MESSAGE : undefined
        }
      />
    </div>
  );
}
